import { CreditorState } from '@debbie/common/dist/creditor/index.js'
import {
  EmbedPlacement,
  getEmbedAreas,
} from '@debbie/common/dist/embeds/index.js'
import { useCollector } from '@debbie/cortex/dist/collectors/index.js'
import {
  useAllCreditors,
  useCreditor,
} from '@debbie/cortex/dist/creditors/index.js'
import { c, h } from '@debbie/cortex/dist/index.js'
import { useCaseFilter } from '@debbie/cortex/dist/insights/caseFilter.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { useTenant, useTenantId } from '@debbie/cortex/dist/tenant/index.js'
import Archive from '@debbie/iris/dist/components/Icons/Archive.js'
import BarChart2 from '@debbie/iris/dist/components/Icons/BarChart2.js'
import BatteryCharging from '@debbie/iris/dist/components/Icons/BatteryCharging.js'
import ExternalLink from '@debbie/iris/dist/components/Icons/ExternalLink.js'
import HardDrive from '@debbie/iris/dist/components/Icons/HardDrive.js'
import HomeIcon from '@debbie/iris/dist/components/Icons/Home.js'
import { Book, Bookmark } from '@debbie/iris/dist/components/Icons/index.js'
import SettingsIcon from '@debbie/iris/dist/components/Icons/Settings.js'
import UsersIcon from '@debbie/iris/dist/components/Icons/Users.js'
import {
  ContentRight,
  LeftNav,
  RightNav,
} from '@debbie/iris/dist/components/index.js'
import { MenuLink } from '@debbie/iris/dist/components/LeftNav.js'
import { BASIC_THEME, updateTheme } from '@debbie/iris/dist/theme.js'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router-dom'

import Cancel from './assets/images/cancel.svg'
import Error from './components/Error.js'
import { FILEBASE } from './config.js'
import Billing from './pages/Billing.js'
import CreateCase from './pages/cases/create/CreateCase.js'
import { Case, Cases } from './pages/cases/index.js'
import CaseVouchers from './pages/CaseVouchers.js'
import Customer from './pages/Customer.js'
import { EmbedsPage, Home, Reports, Settings } from './pages/index.js'
import CreditorIntegration from './pages/integrations/CreditorIntegration.js'
import CreditorIntegrations from './pages/integrations/CreditorIntegrations.js'
import IntegrationsPage from './pages/integrations/IntegrationsPage.js'
import TodoQueue from './pages/TodoQueue.js'

const LoggedInView = () => {
  const redirectTo = useSelector((s: RootState) => s.redirectTo)
  const selectedCreditor = useSelector((s: RootState) => s.selectedCreditor)
  const lang = useSelector((s: RootState) => s.lang)
  const tenantId = useTenantId()
  const allCreditorsLoadable = useAllCreditors(!tenantId)
  const allCreditors = h.hasData(allCreditorsLoadable)
    ? allCreditorsLoadable.data
    : null
  const tenant = useTenant()
  const rightNavOpen = useSelector((s: RootState) => s.rightNavOpen)
  const leftNavOpen = useSelector((s: RootState) => s.leftNavOpen)
  const caseFilter = useCaseFilter()
  const creditorIds = caseFilter.creditorIds ?? []
  const creditorLoadable = useCreditor(selectedCreditor)
  const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null
  const collectorLoadable = useCollector(tenant?.collectorIdentityId)
  const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null

  updateTheme(BASIC_THEME)

  const _creditor = allCreditors
    ? allCreditors.find((creditor) => creditor.creditorId === selectedCreditor)
    : null

  const embedAreas = tenant
    ? getEmbedAreas(tenant.embeds, EmbedPlacement.CreditorWebMenuItem)
    : []

  const embedAreaLinks = embedAreas.length > 0 && {
    text: lang.embeds.creditorNavMenuItemCategory,
    exact: false,
    path: `/embeds/${embedAreas[0].embedId}`,
    links: embedAreas.map((embedArea) => ({
      text: embedArea.title,
      path: `/embeds/${embedArea.embedId}`,
      icon: <Bookmark />,
    })),
  }

  return _creditor && _creditor.state === CreditorState.Cancelled ? (
    <Error
      showIcon={true}
      image={Cancel}
      header={lang.creditors.cancelledError}
      error={lang.creditors.cancelledErrorMessage}
      collector={collector}
    ></Error>
  ) : (
    <Switch>
      {tenantId && (
        <div>
          {redirectTo.to !== '' && <RedirectTo {...redirectTo} />}
          <LeftNav
            logo={
              collector && collector.logoFileId
                ? `${FILEBASE}/${collector.logoFileId}/logo`
                : null
            }
            links={(
              [
                {
                  text: lang.navigation.home,
                  path: '/',
                  links: [
                    {
                      text: lang.navigation.home,
                      path: '/',
                      icon: <HomeIcon />,
                    },
                    {
                      text: lang.navigation.cases,
                      path: '/cases',
                      icon: <Archive />,
                    },
                  ],
                },
                {
                  text: lang.navigation.content,
                  path: '/reports/downloads',
                  links: [
                    {
                      text: lang.navigation.reports,
                      path: '/reports/downloads',
                      icon: <BarChart2 />,
                    },
                  ],
                },
                {
                  text: lang.navigation.economy,
                  path: '/economy/billing',
                  links: [
                    {
                      text: lang.navigation.economyBilling,
                      path: '/economy/billing',
                      icon: <BatteryCharging />,
                    },
                    {
                      text: lang.navigation.economyCaseVouchers,
                      path: '/economy/case-vouchers',
                      icon: <Book />,
                    },
                  ],
                },
              ] as MenuLink[]
            )
              .concat(embedAreaLinks)
              .concat([
                {
                  text: lang.navigation.settings,
                  path: '/settings',
                  links: [
                    ...(creditor && creditor.allowedIntegrations.length > 0
                      ? [
                          {
                            text: lang.navigation.integrations,
                            path: '/integrations',
                            icon: <ExternalLink />,
                          },
                        ]
                      : []),
                    {
                      text: lang.navigation.myAccount,
                      path: '/settings',
                      icon: <SettingsIcon />,
                    },
                    {
                      text: lang.navigation.settingsUsers,
                      path: '/settings/users',
                      icon: <UsersIcon />,
                    },
                    {
                      text: lang.navigation.settingsDevelopers,
                      path: '/settings/developers',
                      // Stays active on the webhook subpages
                      exact: false,
                      icon: <HardDrive />,
                    },
                  ],
                },
              ])}
          />
          {rightNavOpen && <RightNav />}

          <ContentRight leftNavOpen={leftNavOpen} rightNavOpen={rightNavOpen}>
            <Route exact path="/">
              <Home />
            </Route>
            <Route exact path="/cases">
              <Cases />
            </Route>
            <Route exact path="/cases/:ids">
              <Case />
            </Route>
            <Route exact path="/create-case">
              <CreateCase />
            </Route>
            <Route exact path="/todo-queue/:updateTypeId/:id">
              <TodoQueue />
            </Route>
            <Route exact path="/todo-queue/:updateTypeId">
              <TodoQueue />
            </Route>
            <Route exact path="/create-case/:caseId/:slug">
              <CreateCase />
            </Route>
            <Route exact path="/economy/case-vouchers">
              <CaseVouchers />
            </Route>
            <Route exact path="/economy/billing">
              <Billing />
            </Route>
            <Route path="/reports">
              <Reports />
            </Route>
            <Route path="/settings">
              <Settings />
            </Route>
            <Route path="/embeds/:id">
              <EmbedsPage
                context={{
                  creditorId: creditorIds[0],
                }}
              />
            </Route>
            <Route
              path="/integrations"
              exact
              render={() => <IntegrationsPage />}
            />
            <Route
              path="/:creditorId/integrations"
              exact
              render={({ match }) => (
                <CreditorIntegrations creditorId={match.params.creditorId} />
              )}
            />
            <Route
              exact
              path={'/:creditorId/integrations/:id'}
              render={({ match, location }) => {
                const query = new URLSearchParams(location.search)
                return (
                  <CreditorIntegration
                    id={match.params.id}
                    creditorId={match.params.creditorId}
                    token={
                      query.get('token') != null
                        ? query.get('token')
                        : undefined
                    }
                    options={
                      query.get('options') != null
                        ? JSON.stringify(query.get('options'))
                        : undefined
                    }
                  />
                )
              }}
            />
            <Route exact path={'/creditors/:creditorId/customers/:customerId'}>
              {creditor && creditor.tenantId && (
                <Customer creditor={creditor} />
              )}
            </Route>
            <Route
              exact
              path={
                '/creditors/:creditorId/customers/:customerId/users/:userId'
              }
            >
              {creditor && creditor.tenantId && (
                <Customer creditor={creditor} />
              )}
            </Route>
          </ContentRight>
        </div>
      )}
    </Switch>
  )
}

export default LoggedInView

type RedirectProps = {
  to: string
  replace: boolean
}

const RedirectTo = (props: RedirectProps) => {
  const to = props.to
  const replace = props.replace

  useEffect(() => {
    c.redirect.redirect('')
  }, [])

  return to && <Redirect push={!replace} to={to} />
}
